import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';

/**
 * Discounts v2 draft-line hooks — candidates, applied stacks, and per-line
 * validation for the OMS v3 review page. All keyed by the draft id; mutations
 * invalidate the v2 surfaces plus the draft detail (facts restamp on apply).
 */

export function useDraftDiscountCandidatesV2(
  draftSalesOrderId: string | undefined,
) {
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.listDraftCandidates.queryOptions({
      input: { draftSalesOrderId: draftSalesOrderId ?? '' },
    }),
    enabled: !!draftSalesOrderId,
  });
}

export function useDraftLineStacksV2(draftSalesOrderId: string | undefined) {
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.getDraftLineStacks.queryOptions({
      input: { draftSalesOrderId: draftSalesOrderId ?? '' },
    }),
    enabled: !!draftSalesOrderId,
  });
}

/**
 * Per-line Discounts v2 validation — the review page's status column source.
 * Results keyed by line-item id.
 */
export function useValidateDraftDiscountsV2(
  draftSalesOrderId: string | undefined,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.discountsV2.validateDraftDiscounts.queryOptions({
      input: { draftSalesOrderId: draftSalesOrderId ?? '' },
    }),
    enabled: !!draftSalesOrderId,
  });
  return { ...query, results: query.data?.results };
}

/** Invalidate every v3 surface a stack change can move (facts, lifecycle). */
function useInvalidateDraftDiscountSurfaces(draftSalesOrderId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.discountsV2.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.salesOrdersV3.get.queryKey({
        input: { id: draftSalesOrderId },
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.draftSalesOrders.key(),
    });
  };
}

export function useApplyLineStackV2(draftSalesOrderId: string) {
  const invalidate = useInvalidateDraftDiscountSurfaces(draftSalesOrderId);
  return useMutation(
    orpc.accountsReceivable.discountsV2.applyLineStack.mutationOptions({
      onSuccess: () => invalidate(),
    }),
  );
}

export function useRemoveLineStackV2(draftSalesOrderId: string) {
  const invalidate = useInvalidateDraftDiscountSurfaces(draftSalesOrderId);
  return useMutation(
    orpc.accountsReceivable.discountsV2.removeLineStack.mutationOptions({
      onSuccess: () => invalidate(),
    }),
  );
}
